/**
 * Copyright © 2025-2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useState, useEffect } from "react";
import {
    Box,
    Paper,
    Typography,
    Button,
    Divider
} from "@mui/material";
import apiHeaders from "./apiHeaders";
import apiFetch from "./apiFetch";
import LinkButton from './LinkButton';
import { useParams, useLocation } from "react-router-dom";
import { setRealmId } from "./storageUtils";

function Integration() {
    const [error, setError] = useState("");
    const storedCompanyName = sessionStorage.getItem("companyName");
    const [companyName, setCompanyName] = useState(storedCompanyName || "");
    const realmId = useParams();
    const location = useLocation();

    // Retrieving realm id from url and saving to session storage
    useEffect(() => {
        if (realmId && realmId.realmId && realmId.realmId !== ":realmId") {
            setRealmId(realmId.realmId);
            window.dispatchEvent(new Event("realmIdChange"));
        }
    }, [location.search, realmId.realmId]);

    const handleTokenSubmit = async (event) => {
        // Placeholder
    };

    const [isConnected, setIsConnected] = useState(!!sessionStorage.getItem('realmId'));

    // Keep connection/company state in sync with sessionStorage whenever
    // "realmIdChange" fires (login writing a new realmId, logout/disconnect
    // clearing it) without requiring a route change to remount this component.
    //
    // Important: this also re-reads sessionStorage once immediately on mount,
    // rather than relying solely on catching the event. Effects run in
    // declaration order, and the realmId-from-URL effect above dispatches
    // "realmIdChange" synchronously *before* this effect's listener is
    // attached - so on a fresh login redirect that dispatch would otherwise
    // fire with no listener yet registered and be silently missed, leaving
    // isConnected stuck at its stale initial value.
    useEffect(() => {
        const syncFromStorage = () => {
            setIsConnected(!!sessionStorage.getItem('realmId'));
            setCompanyName(sessionStorage.getItem('companyName') || "");
        };

        syncFromStorage();

        window.addEventListener("realmIdChange", syncFromStorage);
        return () => window.removeEventListener("realmIdChange", syncFromStorage);
    }, []);

    const [buttonImage, setButtonImage] = useState("/C2QB_green_btn_tall_default.png");
    
    const handleMouseEnter = () => {
        setButtonImage("/C2QB_green_btn_tall_hover.png");
    };

    const handleMouseLeave = () => {
        setButtonImage("/C2QB_green_btn_tall_default.png");
    };

    function cleanIntegrationUrl() {
        const pathSegments = window.location.pathname.split('/').filter(Boolean);
        if (pathSegments[0] === 'integration' && pathSegments.length > 1) {
            const newUrl = `${window.location.origin}/integration${window.location.search}`;
            window.history.replaceState({}, '', newUrl);
        }
    }

    const fetchCompanyName = async () => {
        try {
            const data = await apiFetch(`/api/company?realmId=${sessionStorage.getItem('realmId')}`);
            
            if (data === null) return; // Session expired, handled by apiFetch

            setCompanyName(data.companyName);
            sessionStorage.setItem("companyName", data.companyName);
        } catch (err) {
            console.error("Failed to fetch company name", err);
        }
    };

    const handleConnect = async () => {
        const isConnected = !!sessionStorage.getItem('realmId');
        if (isConnected) {
            // Disconnect logic
            const result = await apiFetch(
                `/api/revoke?realmId=${sessionStorage.getItem('realmId')}`
            );

            if (result === null) return; // Session expired, handled by apiFetch

            sessionStorage.clear();
            setIsConnected(false);
            cleanIntegrationUrl();
        } else {
            // Connect logic - this doesn't need auth
            const res = await fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/auth/connect`);
            const data = await res.json();
            window.location.href = data.url;
        }
        window.dispatchEvent(new Event("realmIdChange"));
    };

    useEffect(() => {
        const fetchToken = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const sessionId = urlParams.get("s_id");

            // Only refetch the company name for an already-connected session.
            // Without this guard, a cleared session (e.g. right after logout)
            // still triggers this call, the backend returns 401, and apiFetch
            // misreports it as an expired session instead of "not connected".
            const hasActiveSession = !!sessionStorage.getItem("token") && !!sessionStorage.getItem("realmId");
            if (hasActiveSession && !sessionStorage.getItem("companyName")) {
                fetchCompanyName();
            }

            if (sessionId) {
                try {
                    // Session exchange doesn't need auth token
                    const response = await fetch(
                        `${process.env.REACT_APP_CONNECTOR_API_URL}/api/session?sessionId=${sessionId}`,
                        {
                            method: "GET",
                            headers: apiHeaders
                        }
                    );
                    if (response.ok) {
                        const result = await response.json();
                        sessionStorage.setItem("token", result.token);
                        if (!sessionStorage.getItem("companyName")) {
                            fetchCompanyName();
                        }
                    } else {
                        const result = await response.json();
                        setError(result.message);
                    }
                } catch (err) {
                    setError("Internal server error. Please try again.");
                }
                
                // Remove sessionId from URL without reloading
                urlParams.delete("s_id");
                const newUrl = `${window.location.pathname}${urlParams.toString() ? "?" + urlParams.toString() : ""}`;
                window.history.replaceState({}, "", newUrl);
            }
        };
        fetchToken();
    }, []);

    return (
        <Box display="flex" flexDirection="column" gap={2} p={2}>
            <Box sx={{ alignItems: "center" }}>
                <Typography
                    variant="h6"
                    sx={{ mb: 2, fontWeight: "bold", textAlign: "center" }}
                >
                    QuickBooks Online with Zamp
                </Typography>
            </Box>

            <Box component="form" onSubmit={handleTokenSubmit}>
                <Paper elevation={6} sx={{ p: 4, maxWidth: 800, margin: "auto" }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        In order to use the Zamp Tax app for QuickBooks Online, you must connect your QuickBooks Online account. The Zamp Tax app's configurations will not be accessible until your QuickBooks Online account has been authenticated.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        You may choose to launch the Zamp Tax integration from your "Apps" menu, or connect via the button below.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        If you are already connected and would like to disconnect the Zamp Tax app from QuickBooks Online, please choose the "Disconnect" link below.
                    </Typography>
                    <Typography variant="body1">
                        If you need assistance, please contact{" "}
                        <a
                            href="mailto:support@zamp.com"
                            style={{ color: "#1976d2", textDecoration: "none" }}
                        >
                            support@zamp.com
                        </a>.
                    </Typography>

                    <Divider sx={{ my: 3 }} />

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            mt: 3,
                            position: "relative",
                            minHeight: "60px",
                        }}
                    >
                        {/* Logo on the left */}
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <img
                                src="/qbio-logo2.png"
                                alt="QuickBooks Logo"
                                style={{ height: "40px" }}
                            />
                        </Box>

                        {/* Disconnect text+button on the left (if connected) */}
                        {isConnected && (
                            <Box sx={{ ml: 2, display: "flex", alignItems: "center", flexGrow: 1 }}>
                                <Typography variant="body1" sx={{ mr: 1 }}>
                                    You are connected to QuickBooks
                                    {companyName && (
                                        <Box component="span" sx={{ fontStyle: "italic" }}>
                                            {" "}({companyName})
                                        </Box>
                                    )}.
                                </Typography>
                                <LinkButton onClick={handleConnect}>Disconnect</LinkButton>
                            </Box>
                        )}

                        {/* Connect button centered (if not connected) */}
                        {!isConnected && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                }}
                            >
                                <Button
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        padding: "6px 12px",
                                        textTransform: "none",
                                    }}
                                    onClick={handleConnect}
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    <img
                                        src={buttonImage}
                                        alt="Connect to QuickBooks"
                                        style={{ height: 40 }}
                                    />
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
}

export default Integration;
